/**
 * InlineUdfFields — renders UDF fields as grid items inside the parent
 * MatrixFieldsRoot. Reuses hooks and components from FormUdfRenderer
 * to share the same dedup-only fetch logic (no persistent cache).
 *
 * Form state matches the API: `userDefinedFields: [{ name, value, moduleId }]`.
 */
import React, { useEffect, useMemo } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import type { FormUdfNode } from '../../types/layout';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { MatrixFieldShell, MatrixSectionDivider } from './MatrixFieldsMesh';
import { useUdfFields, toFieldKey, UdfFieldControl, useSeedUdfArray, UdfFieldViewControl } from './FormUdfRenderer';

export interface InlineUdfFieldsProps {
  node: FormUdfNode;
  namePrefix?: string;
  columnCount: number;
  sectionDividerIndex: number;
  /** First CSS grid row for this UDF block (divider row). Fields start on gridRowStart + 1. */
  gridRowStart: number;
}

export const InlineUdfFields = React.memo(function InlineUdfFields({
  node,
  namePrefix,
  columnCount,
  sectionDividerIndex,
  gridRowStart,
}: InlineUdfFieldsProps): React.ReactElement | null {
  const { form, udfRequiredFieldsRef } = useFormEngineForm();
  const { isReadOnly, viewPresentation } = useFormEngineState();
  const useViewMode = isReadOnly && viewPresentation !== 'disabled-inputs';
  const title = node.title ?? 'Other Information';
  const basePrefix = node.fieldPrefix ?? 'userDefinedFields';
  const prefix = namePrefix ? `${namePrefix}.${basePrefix}` : basePrefix;

  const { fields, loading } = useUdfFields(node.actionId, node.moduleId);

  const fieldKeys = useMemo(
    () => fields.map((f) => ({ ...f, key: toFieldKey(f.fieldName) })),
    [fields],
  );

  useSeedUdfArray(form, prefix, node.moduleId, fieldKeys);

  useEffect(() => {
    const registry = udfRequiredFieldsRef.current;
    const registeredPaths: string[] = [];

    fieldKeys.forEach((field, idx) => {
      if (field.isMandatory) {
        const path = `${prefix}.${idx}.value`;
        registry.set(path, 'Required field');
        registeredPaths.push(path);
      }
    });

    return () => {
      for (const path of registeredPaths) {
        registry.delete(path);
      }
    };
  }, [fieldKeys, prefix, udfRequiredFieldsRef]);

  if (loading || fieldKeys.length === 0) return null;

  const control = form.control as unknown as Control<FieldValues>;

  const dividerPlacementCSS = `grid-column: 1 / -1; grid-row: ${gridRowStart};`;

  const fieldsPerRow = columnCount;
  const fieldRowOffset = gridRowStart + 1;

  return (
    <>
      {title != null && title !== '' && (
        <MatrixSectionDivider
          key={`udf-divider-${node.moduleId}`}
          title={title}
          titleVariant="heading"
          divider={false}
          placementCSS={dividerPlacementCSS}
          isFirst={sectionDividerIndex === 0}
        />
      )}
      {fieldKeys.map((field, idx) => {
        const name = `${prefix}.${idx}.value`;
        const row = fieldRowOffset + Math.floor(idx / fieldsPerRow);
        const col = (idx % fieldsPerRow) + 1;
        const fieldPlacementCSS = `grid-column: ${col} / span 1; grid-row: ${row};`;

        return (
          <MatrixFieldShell key={`udf-${field.id}`} $placementCSS={fieldPlacementCSS} $isVirtual={false}>
            {useViewMode ? (
              <UdfFieldViewControl field={field} name={name} control={control} viewPresentation={viewPresentation} />
            ) : (
              <UdfFieldControl field={field} name={name} control={control} disabled={isReadOnly} />
            )}
          </MatrixFieldShell>
        );
      })}
    </>
  );
});
